import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faPenToSquare,
  faCircleMinus,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import {
  getAllChargingSites,
  deleteChargingSite,
} from "../../api/ChargingSites";
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import DataTable from "../../components/DataTable";
import TdLink from "../../components/TdLink";
import { useTranslation } from "react-i18next";

const SitesPage = () => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [sites, setSites] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });

  useEffect(() => {
    handleGetSites();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  const handleGetSites = async () => {
    try {
      const accessToken = await getAccessToken();
      const newSites = await getAllChargingSites(accessToken);
      setSites(newSites);
      setSitesLoaded(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteSite = async (chargingSitePublicId) => {
    const toastId = toast.loading(t("toast.deleting", { entity: t("entity.site") }));
    try {
      const accessToken = await getAccessToken();
      await deleteChargingSite(chargingSitePublicId, accessToken);
      const newSites = await getAllChargingSites(accessToken);
      setSites(newSites);
      toast.success(t("toast.deleted", { entity: t("entity.site") }), { id: toastId });
    } catch (error) {
      toast.error(t("toast.failedDelete", { entity: t("entity.site") }), { id: toastId });
      console.error(error);
    }
  };

  const columns = [
    {
      key: "chargingSitePublicId",
      header: t("table.siteId"),
      sortable: true,
      rawCell: true,
      headerClassName: "min-w-[180px]",
      getSortValue: (site) => site.chargingSitePublicId ?? "",
      render: (site) => (
        <TdLink
          className="min-w-[180px]"
          to={`/site/${site.chargingSitePublicId}`}
        >
          {site.chargingSitePublicId || ""}
        </TdLink>
      ),
    },
    {
      key: "name",
      header: t("common.name"),
      sortable: true,
      rawCell: true,
      getSortValue: (site) => site.name ?? "",
      render: (site) => (
        <TdLink to={`/site/${site.chargingSitePublicId}`}>
          {site.name || ""}
        </TdLink>
      ),
      },
      {
          key: "isNotificationEnabled",
          header: t("table.notificationsEnabled"),
          sortable: true,
          rawCell: true,
          getSortValue: (site) => site.isNotificationEnabled ?? "",
          render: (site) => (
              <TdLink to={`/site/${site.chargingSitePublicId}`}>
                  {site.isNotificationEnabled ? t("common.yes") : t("common.no")}
              </TdLink>
          ),
      },
    {
      key: "actions",
      header: "",
      sortable: false,
      searchable: false,
      rawCell: true,
      headerClassName: "min-w-[64px]",
      render: (site) => (
        <td className="min-w-[64px]">
          <div className="flex justify-end">
            <CustomDropdown
              isRight={true}
              ContentComponent={() => (
                <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                  <button
                    className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                    onClick={() => {
                      navigate(`/site/${site.chargingSitePublicId}`);
                    }}
                  >
                    <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                  </button>
                  <button
                    className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                    onClick={() => {
                      setConfirmState({
                        isOpen: true,
                        title: t("confirm.deleteEntity", {
                          entity: t("entity.site"),
                          name: site.name,
                        }),
                        confirm: () => {
                          handleDeleteSite(site.chargingSitePublicId);
                        },
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                  </button>
                </div>
              )}
            >
              <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                <FontAwesomeIcon icon={faEllipsis} />
              </div>
            </CustomDropdown>
          </div>
        </td>
      ),
    },
  ];

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute inset-0 pointer-events-none">
              <LoadingTablePage title={t("entity.sites")} loading={!sitesLoaded} />
            </div>

            <div className={`${!sitesLoaded ? "loading" : ""} page-content`}>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">{t("entity.sites")}</h2>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    navigate(`/site/create`);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> {t("pages.createSite")}
                </button>
              </div>

              <div className="rounded-md shadow-md w-full p-4">
                <DataTable
                  rows={sites}
                  columns={columns}
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder={t("search.byIdOrName")}
                  defaultSortColumn="chargingSitePublicId"
                  rowKey={(site, index) => site.chargingSitePublicId || index}
                  emptyColSpan={4}
                />
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
        }}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default SitesPage;
